import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@components/ui/button'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

const AnimatedTextarea = React.forwardRef(({ value, onChange, onKeyDown, onSubmit, className, ...props }, ref) => {
  const [placeholder, setPlaceholder] = useState('')
  const fullText = 'What would you like to create? Describe your vision...'
  const textareaRef = useRef(null)

  // Use the forwarded ref or fallback to local ref
  const actualRef = ref || textareaRef

  useEffect(() => {
    let currentIndex = 0
    let direction = 1 // 1 for typing, -1 for deleting
    let pauseCounter = 0
    const pauseDuration = 30 // How long to pause at full text (in animation frames)

    const animatePlaceholder = () => {
      if (direction === 1) {
        // Typing
        if (currentIndex < fullText.length) {
          setPlaceholder(fullText.substring(0, currentIndex + 1))
          currentIndex++
        } else {
          // Reached the end, pause before deleting
          if (pauseCounter < pauseDuration) {
            pauseCounter++
          } else {
            pauseCounter = 0
            direction = -1
          }
        }
      } else {
        // Deleting (only delete back to "What would you like to create?")
        const baseText = 'What would you like to create?'
        if (currentIndex > baseText.length) {
          setPlaceholder(fullText.substring(0, currentIndex - 1))
          currentIndex--
        } else {
          // Reached the base text, pause before typing again
          if (pauseCounter < pauseDuration) {
            pauseCounter++
          } else {
            pauseCounter = 0
            direction = 1
          }
        }
      }
    }

    const intervalId = setInterval(animatePlaceholder, 100)
    return () => clearInterval(intervalId)
  }, [])

  // Handle textarea input change and auto-resize
  const handleTextareaChange = useCallback(
    (e) => {
      const textarea = e.target
      if (onChange) {
        onChange(e)
      }

      // Reset height to measure actual content height
      textarea.style.height = '50px'

      // Only expand if content actually overflows the initial height
      if (textarea.scrollHeight > 50) {
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
      }
    },
    [onChange]
  )

  // Handle quick submit button click
  const handleQuickSubmit = useCallback(
    (e) => {
      e.preventDefault()
      if (onSubmit) {
        onSubmit(e)
      }
    },
    [onSubmit]
  )

  return (
    <div className="relative p-3 sm:p-4 md:p-6 pb-2 z-10">
      <textarea
        ref={actualRef}
        className={cn(
          'w-full py-2 sm:py-3 px-3 sm:px-4 pr-8 sm:pr-10 text-sm sm:text-base border resize-none overflow-y-auto text-white/80 bg-transparent border-gray-200 focus:ring-1 focus:ring-purple-400 focus:border-purple-400 placeholder:text-gray-400',
          className
        )}
        placeholder={placeholder}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={onKeyDown}
        style={{ height: '50px', maxHeight: '200px' }}
        aria-label="Describe your content creation vision"
        id="prompt-textarea"
        autoComplete="off"
        autoFocus
        {...props}
      />
      <div className="absolute right-4 sm:right-5 md:right-7 top-4 sm:top-5 md:top-7">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-full h-8 w-8 sm:h-10 sm:w-10 p-0 text-purple-500 hover:bg-transparent"
          aria-label="Quick generate content"
          onClick={handleQuickSubmit}
        >
          <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
      </div>
    </div>
  )
})

AnimatedTextarea.displayName = 'AnimatedTextarea'

export default AnimatedTextarea
